import { toColumnMajor } from './homography.js';
/**
 * **曲面の枠を GL に流す**(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 4・5)。
 *
 * 表示([`webgl-renderer.ts`](./webgl-renderer.ts))と書き出し([`compose.ts`](./compose.ts))が**同じ手順**を使うよう、
 * ここに 1 つだけ置く —— ステンシルの手順は片方だけ直すと見え方が食い違う。何を描くかの判断は
 * [`curvedDrawPlan`](./curve.ts)(純関数)にあり、ここは GL を呼ぶだけ。WebGL は jsdom で試さない(ADR-0019)ので、
 * 試験は**呼び出しを記録する偽の GL** で手順だけを確かめる(`curve-draw.test.ts`)。
 *
 * ## 手順
 *
 * 1. `uPanelSize` に展開図全体の寸法を渡す
 * 2. **内向きで裏の片があれば**: ステンシルを消し、裏の片を**ステンシルにだけ**書く(色は書かない)
 * 3. 表の片を、セルごとに素材を結び付けて(`prepareCell`)、**片ごとに `uInverse` だけを替えて**描く。
 *    2 を通ったときは**ステンシルの外だけ**に描く(手前の裏に隠れた部分を描かない)
 * 4. 色の書き込みを戻し、ステンシルを止める —— **平面・折れた枠の描画に状態を漏らさない**(途中で投げても戻す)
 *
 * **シェーダは変えない**(決定 4)。`uContentRect` と素材は `prepareCell` がセルごとに渡す(転送は素材ごとに 1 回のまま)。
 *
 * **ステンシルを持たないコンテキスト**(`stencil: true` を求めても得られなかった)では隠れを諦め、表の片をそのまま描く ——
 * 手前の裏に隠れるはずの素材が透けて見えるが、何も描かないよりは盤面の位置が分かる。
 */
export function drawCurvedPlan(gl, program, plan, 
/** セルの素材を結び付け、`uContentRect` を渡す。**描けないセル(素材が無い・準備前)は `false`。** */
prepareCell) {
    if (plan.cells.length === 0)
        return;
    gl.uniform2f(program.uniforms.panelSize, plan.panel.width, plan.panel.height);
    const hide = plan.occluders.length > 0 && gl.getContextAttributes()?.stencil === true;
    try {
        if (hide) {
            gl.enable(gl.STENCIL_TEST);
            gl.stencilMask(0xff);
            gl.clearStencil(0);
            gl.clear(gl.STENCIL_BUFFER_BIT);
            // 裏の片はステンシルに 1 を書くだけ。色は書かない。
            gl.stencilFunc(gl.ALWAYS, 1, 0xff);
            gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
            gl.colorMask(false, false, false, false);
            for (const piece of plan.occluders)
                drawPiece(gl, program, piece);
            gl.colorMask(true, true, true, true);
            // 表の片はステンシルが 0 の画素(手前に裏の片が無いところ)だけに描く。
            gl.stencilFunc(gl.EQUAL, 0, 0xff);
            gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
        }
        for (const { cell, pieces } of plan.cells) {
            if (!prepareCell(cell))
                continue;
            for (const piece of pieces)
                drawPiece(gl, program, piece);
        }
    }
    finally {
        if (hide) {
            gl.colorMask(true, true, true, true);
            gl.disable(gl.STENCIL_TEST);
        }
    }
}
/**
 * 片を 1 つ描く。`uInverse` を片のものに替え、4 隅を三角形 2 つで塗る。
 *
 * **4 隅は割り付けの値そのもの**(隣の片と同じ値)—— GPU の塗りつぶし規則により、辺を共有する三角形は
 * 境界の画素を過不足なく 1 度だけ塗る。**隣と同じ値の頂点を渡せば隙間も重なりも出ない。**
 */
function drawPiece(gl, program, piece) {
    gl.uniformMatrix3fv(program.uniforms.inverse, false, toColumnMajor(piece.inverse));
    const [a, b, c, d] = piece.corners;
    gl.bindBuffer(gl.ARRAY_BUFFER, program.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([...a, ...b, ...c, ...a, ...c, ...d]), gl.DYNAMIC_DRAW);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
}
//# sourceMappingURL=curve-draw.js.map